import { RoadmapResult } from '../core/Api';
import createEventSignal from '../core/Signal';
import { APPLICATION_TASK_TAB_PAD_HEIGHT, COLLAPSED_ROW_HEIGHT, INNER_Y_PADDING, MIN_EXPANDED_ROW_HEIGHT, ORGANIZATION_APPPLICATION_GUTTER, ORGANIZATION_MIN_EXPANDED_GROUP_HEIGHT, TASK_CELL_PADDING, TASK_HEIGHT, UNASSIGNED_COLUMNS, UNASSIGNED_GAP, UNASSIGNED_Y_PADDING } from '../layout';
import { Solution, SolutionVersion, DateRange, TaskCategory, Organization, Release, Task, Filter } from '../types';
import { groupBy as groupArrayBy } from '../utils/arrayUtils';
import { getEndOfYear, getStartOfYear } from '../utils/dateUtils';

// Signals
export const [onFiltersChange, dispatchFiltersChange] = createEventSignal<TaskCategory[]>();
export const [onExpandedColumnsChange, dispatchOnExpandedColumnsChange] = createEventSignal<number[]>();
export const [onExpandedRowsChange, dispatchOnExpandedRowsChange] = createEventSignal<number[]>();
export const [onDateRangeChange, dispatchOnDateRangeChange] = createEventSignal<DateRange>();
export const [onGroupByChange, dispatchOnGroupByChange] = createEventSignal<'solution' | 'organization'>();
export const [onRowHeightsChange, dispatchOnRowHeightsChange] = createEventSignal<Record<number, number>>();
export const [onFilteredTasksChange, dispatchOnFilteredTasksChange] = createEventSignal<Task[]>();
export const [onTasksChange, dispatchOnTasksChange] = createEventSignal<Task[]>();
export const [onSelectTaskChange, dispatchOnSelectTaskChange] = createEventSignal<Task | null>();
export const [onIsAuthenticatedChange, dispatchIsAuthenticatedChange] = createEventSignal<boolean>();

let dateRange: DateRange = [
  getStartOfYear(),
  getEndOfYear(),
];
let filters: Filter[] = ['error', 'support', 'request', 'maintenance', 'documentation'];
let groupBy: 'solution' | 'organization' = 'solution';
let expandedColumns: number[] = [];
let expandedRows: number[] = [];

let selectedTask: Task | null = null;
let tasks: Task[] = [];
let releases: Release[] = [];
let organizations: Organization[] = [];
let solutions: Solution[] = [];
let solutionVersions: SolutionVersion[] = [];
let rowHeights: Record<number, number> = {};
let isAuthenticated: boolean = false;

let solutionVersionsById: Record<number, SolutionVersion> = {};

export const getFilteredTasks = () => {
  return tasks
    .filter((task) => {
      if (filters.includes('exclude-on-hold') && task.statusCode === 'onHold') {
        return false;
      }

      return filters.includes(task.category);
    });
};
export const getTasks = () => tasks;
export const getReleases = () => releases;
export const getOrganizations = () => organizations;
export const getSolutions = () => solutions;
export const getSolutionVersions = () => solutionVersions;
export const getSolutionVersionsById = () => solutionVersionsById;
export const getExpandedColumns = () => expandedColumns;
export const getExpandedRows = () => expandedRows;
export const getFilters = () => filters;
export const getDateRange = () => dateRange;
export const getGroupBy = () => groupBy;
export const getRowHeights = () => rowHeights;
export const getIsAuthenticated = () => isAuthenticated;
export const getSelectedTask = () => selectedTask;

export const calculateRowHeights = () => {
  const heights: Record<number, number> = {};

  if (groupBy === 'organization') {
    const tasks = groupArrayBy(getTasks(), (item) => `${item.organizationId}-${item.releaseId || 'UNASSIGNED'}`);

    for (const organization of organizations) {
      const isExpanded = expandedRows.includes(organization.id);
      let maxHeight = COLLAPSED_ROW_HEIGHT as number;

      if (isExpanded) {
        maxHeight = ORGANIZATION_MIN_EXPANDED_GROUP_HEIGHT;

        // Unassigned tasks
        const unassignedTasks = tasks[`${organization.id}-UNASSIGNED`];

        if (unassignedTasks) {
          const numberOfRows = Math.ceil(unassignedTasks.length / UNASSIGNED_COLUMNS);
          const unassignedTasksHeight = (
            (numberOfRows - 1) * UNASSIGNED_GAP +
            (numberOfRows * TASK_HEIGHT) +
            UNASSIGNED_Y_PADDING * 2
          );
          if (unassignedTasksHeight > maxHeight) {
            maxHeight = unassignedTasksHeight;
          }
        }

        for (const release of releases) {
          const filteredTasks = tasks[`${organization.id}-${release.id}`];

          if (filteredTasks) {
            const numberOfSolutions = Object.keys(
              groupArrayBy(
                filteredTasks,
                (item) => `${item.solutionId}`
              )
            ).length;

            const height = (
              (INNER_Y_PADDING * 2) +
              (numberOfSolutions * TASK_HEIGHT) +
              ((numberOfSolutions - 1) * ORGANIZATION_APPPLICATION_GUTTER) +
              (filteredTasks.length * TASK_HEIGHT) +
              APPLICATION_TASK_TAB_PAD_HEIGHT
            );

            if (height > maxHeight) {
              maxHeight = height;
            }
          }
        }
      }

      heights[organization.id] = maxHeight;
    }

    return heights;
  }

  for (const solution of solutions) {
    const isExpanded = expandedRows.includes(solution.id);

    if (isExpanded) {
      let maxHeight = MIN_EXPANDED_ROW_HEIGHT as number;

      for (const release of releases) {
        const key = `${solution.id}-${release.id}`;
        const tasks = groupArrayBy(getTasks(), (item) => `${item.solutionId}-${item.releaseId}`)[key];
        const taskCount = tasks?.length || 0;
        const height = TASK_CELL_PADDING + TASK_HEIGHT * taskCount;

        if (height > maxHeight) {
          maxHeight = height;
        }
      }

      heights[solution.id] = maxHeight + 32;
    } else {
      heights[solution.id] = COLLAPSED_ROW_HEIGHT;
    }
  }

  return heights;
};

// Actions
export const reset = (data: RoadmapResult) => {
  tasks = data.tasks;
  releases = data.releases;
  organizations = data.organizations;
  solutions = data.solutions;
  solutionVersions = data.solutionVersions;
  rowHeights = calculateRowHeights();

  releases.sort((a, b) => a.releaseDate.getTime() - b.releaseDate.getTime());
  solutions.sort((a, b) => a.name.localeCompare(b.name));
  organizations.sort((a, b) => a.name.localeCompare(b.name));

  solutionVersionsById = {};

  for (const solutionVersion of solutionVersions) {
    solutionVersionsById[solutionVersion.id] = solutionVersion;
  }

  if (expandedColumns.length === 0) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    const currrentRelease = releases
      .find((release) => release.releaseDate.getFullYear() === currentYear && release.releaseDate.getMonth() === currentMonth);

    if (currrentRelease) {
      toggleColumn(currrentRelease.id);
    }
  }

  const nextIsAuthenticated = data.user ? true : false;
  if (isAuthenticated !== nextIsAuthenticated) {
    isAuthenticated = nextIsAuthenticated;
    dispatchIsAuthenticatedChange(isAuthenticated);
  };

  dispatchOnTasksChange(tasks);
  dispatchOnFilteredTasksChange(getFilteredTasks());
};

export const setDateRange = (newDateRange: DateRange) => {
  dateRange = newDateRange;
  dispatchOnDateRangeChange(newDateRange);
};

export const setFilters = (newFilters: TaskCategory[]) => {
  filters = newFilters;
  dispatchFiltersChange(newFilters);
  dispatchOnFilteredTasksChange(getFilteredTasks());
};

export const setGroupBy = (newGroupBy: 'solution' | 'organization') => {
  groupBy = newGroupBy;
  rowHeights = calculateRowHeights();

  dispatchOnGroupByChange(newGroupBy);
  dispatchOnRowHeightsChange(rowHeights);
};

export const setIsAuthenticated = (newIsAuthenticated: boolean) => {
  isAuthenticated = newIsAuthenticated;
  dispatchIsAuthenticatedChange(newIsAuthenticated);
};

export const toggleColumn = (column: number, exclusive = false) => {
  if (exclusive) {
    expandedColumns = [];
  }

  const isExpanded = expandedColumns.includes(column);

  if (isExpanded) {
    expandedColumns = expandedColumns.filter((c) => c !== column);
  } else {
    expandedColumns = [...expandedColumns, column];
  }

  dispatchOnExpandedColumnsChange(expandedColumns);
};

export const toggleRow = (row: number, exclusive = false) => {
  if (exclusive) {
    expandedRows = [];
  }

  const isExpanded = expandedRows.includes(row);

  if (isExpanded) {
    expandedRows = expandedRows.filter((r) => r !== row);
  } else {
    expandedRows = [...expandedRows, row];
  }

  rowHeights = calculateRowHeights();
  dispatchOnExpandedRowsChange(expandedRows);
  dispatchOnRowHeightsChange(rowHeights);
};

export const selectTask = (task: Task | null | undefined) => {
  selectedTask = task || null;
  dispatchOnSelectTaskChange(task);
};

export const scrollTaskIntoView = (taskId: number) => {
  const foundTask = tasks.find((task) => task.id === taskId);
  if (!foundTask) {
    selectTask(null);
  }

  if (groupBy === 'organization') {
    const organization = organizations.find((org) => org.id === foundTask.organizationId);
    if (!organization) {
      return selectTask(foundTask);
    }

    const isExpanded = expandedRows.includes(organization.id);
    if (!isExpanded) {
      toggleRow(organization.id, true);
    }
  }

  if (groupBy === 'solution') {
    const solution = solutions.find((app) => app.id === foundTask.solutionId);
    if (!solution) {
      return selectTask(foundTask);
    }

    const isExpanded = expandedRows.includes(solution.id);
    if (!isExpanded) {
      toggleRow(solution.id, true);
    }
  }

  const release = releases.find((release) => release.id === foundTask.releaseId);
  if (!release) {
    return selectTask(foundTask);
  }

  toggleColumn(release.id, true);
  return selectTask(foundTask);
};
